import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Typography, Card, Row, Col, Tag, Divider, Button, Spin, Statistic, Alert, Image, Space, Tooltip, Modal } from 'antd';
import { PrinterOutlined, HeartOutlined, StarOutlined, CrownOutlined, SettingOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, EyeOutlined, DownloadOutlined, FireOutlined, CloseOutlined ,
  // 添加新图标
  CalendarOutlined,
  TagOutlined,
  InfoCircleOutlined} from '@ant-design/icons';
import { Carousel } from 'antd';

// 导入打印机页面组件
import PrinterOverviewPage from './components/PrinterOverview/PrinterOverviewPage';
import PrinterDetailPage from './components/PrinterOverview/PrinterDetailPage';

// 导入产品详情页面组件
import ProductDetailPage from './components/ProductDetail/ProductDetailPage';

// 导入管理组件
import AdminLogin from './components/Admin/AdminLogin';
import AdminLayout from './components/Admin/AdminLayout';
import AdminRoutes from './components/Admin';

// 导入搜索组件
import SearchBar from './components/SearchBar';

// 导入推荐栏组件
import FeaturedCarousel from './components/FeaturedCarousel';

// 导入寄语信箱组件
import JiyuMailboxPage from './components/JiyuMailbox';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;



// 获取3D打印成品列表
const fetchPrintedProducts = async (category?: string, searchKeyword?: string) => {
  try {
    let url = '/api/printed_products';
    const params = new URLSearchParams();

    if (category && category !== 'all') {
      params.append('category', category);
    }

    if (searchKeyword && searchKeyword.trim()) {
      params.append('search', searchKeyword.trim());
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch printed products:', error);
    return [];
  }
};

// 导航栏分类数据
const categories = [
  { id: 'all', name: '全部', icon: <StarOutlined />, isSpecial: true },
  { id: 'decorative', name: '装饰品', icon: null },
  { id: 'functional', name: '功能件', icon: null },
  { id: 'toy', name: '玩具', icon: null },
  { id: 'art', name: '艺术品', icon: null },
  { id: 'tool', name: '工具', icon: null },
  { id: 'other', name: '其他', icon: <CrownOutlined />, isSpecial: true },
];

// 产品卡片组件
const ProductCard: React.FC<{ product: any; onCardClick: (product: any) => void }> = ({ product, onCardClick }) => {
  // 从 localStorage 获取初始状态
  const getInitialLikeState = () => {
    try {
      const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
      return likedProducts[product.id] || false;
    } catch {
      return false;
    }
  };

  const getInitialFavoriteState = () => {
    try {
      const favoritedProducts = JSON.parse(localStorage.getItem('favoritedProducts') || '{}');
      return favoritedProducts[product.id] || false;
    } catch {
      return false;
    }
  };

  const [liked, setLiked] = useState(getInitialLikeState());
  const [likeCount, setLikeCount] = useState(product.like_count || 0);
  const [favorited, setFavorited] = useState(getInitialFavoriteState());
  const [favoriteCount, setFavoriteCount] = useState(product.favorite_count || 0);
  
  // 更新 localStorage 中的点赞状态
  const updateLocalStorageLike = (productId: number, isLiked: boolean) => {
    try {
      const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
      likedProducts[productId] = isLiked;
      localStorage.setItem('likedProducts', JSON.stringify(likedProducts));
    } catch (error) {
      console.error('更新 localStorage 失败:', error);
    }
  };

  // 更新 localStorage 中的收藏状态
  const updateLocalStorageFavorite = (productId: number, isFavorited: boolean) => {
    try {
      const favoritedProducts = JSON.parse(localStorage.getItem('favoritedProducts') || '{}');
      favoritedProducts[productId] = isFavorited;
      localStorage.setItem('favoritedProducts', JSON.stringify(favoritedProducts));
    } catch (error) {
      console.error('更新 localStorage 失败:', error);
    }
  };

  const toggleFavoriteAPI = async (productId: number) => {
    try {
      const response = await fetch(`/api/printed_products/${productId}/favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('收藏操作失败');
      }
      return await response.json();
    } catch (error) {
      console.error('收藏操作失败:', error);
      throw error;
    }
  };

  const likeProduct = async (productId: number) => {
    try {
      const response = await fetch(`/api/printed_products/${productId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('点赞失败');
      }
      return await response.json();
    } catch (error) {
      console.error('点赞失败:', error);
      throw error;
    }
  };

  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const newFavorited = !favorited;
      
      // 乐观更新 UI
      setFavorited(newFavorited);
      setFavoriteCount((prev: number) => newFavorited ? prev + 1 : prev - 1);
      
      // 更新 localStorage
      updateLocalStorageFavorite(product.id, newFavorited);
      
      // 调用 API
      await toggleFavoriteAPI(product.id);
    } catch (error) {
      console.error('收藏失败:', error);
      // 回滚 UI 状态
      setFavorited(favorited);
      setFavoriteCount((prev: number) => favorited ? prev : prev - 1);
      // 回滚 localStorage
      updateLocalStorageFavorite(product.id, favorited);
    }
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const newLiked = !liked;
      
      // 乐观更新 UI
      setLiked(newLiked);
      setLikeCount((prev: number) => newLiked ? prev + 1 : prev - 1);
      
      // 更新 localStorage
      updateLocalStorageLike(product.id, newLiked);
      
      // 调用 API
      await likeProduct(product.id);
    } catch (error) {
      console.error('点赞失败:', error);
      // 回滚 UI 状态
      setLiked(liked);
      setLikeCount((prev: number) => liked ? prev : prev - 1);
      // 回滚 localStorage
      updateLocalStorageLike(product.id, liked);
    }
  };

  const handleCardClick = () => {
    onCardClick(product);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'decorative': return '#d32f2f';
      case 'functional': return '#1890ff';
      case 'toy': return '#ff9800';
      case 'educational': return '#4caf50';
      case 'custom': return '#9c27b0';
      default: return '#795548';
    }
  };

  const getCategoryName = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.name : category;
  };

    return (
    <Card
      hoverable
      onClick={handleCardClick}
      style={{
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '4px',
        cursor: 'pointer'
      }}
      bodyStyle={{ padding: '0' }}
      cover={
        <div style={{ 
          position: 'relative', 
          width: '100%',
          paddingBottom: '133.33%',
          overflow: 'hidden',
          backgroundColor: '#fafafa'
        }}>
          {product.images && product.images.length > 0 ? (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Image
                alt={product.title}
                src={product.images[0]}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                preview={false}
              />
            </div>
          ) : (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${getCategoryColor(product.category)}, #ffcdd2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PrinterOutlined style={{ fontSize: 'clamp(24px, 10vw, 36px)', color: 'rgba(255, 255, 255, 0.8)' }} />
            </div>
          )}
          <div style={{
            position: 'absolute',
            top: '1px',
            right: '6px',
            background: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: 'clamp(10px, 3vw, 12px)'
          }}>
            {product.is_featured && <FireOutlined style={{ marginRight: '2px', fontSize: '10px' }} />}
            {getCategoryName(product.category)}
          </div>
        </div>
      }
    >
      <div style={{ padding: '1px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1px', marginTop: '4px' }}>
          <Title level={5} style={{ margin: 0, flex: 1, fontSize: 'clamp(14px, 4vw, 16px)', lineHeight: 1.2 }}>{product.title}</Title>
        </div>
        
        {/* 合并的点赞收藏观看区域 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          fontSize: '12px', 
          color: '#666',
          marginTop: '12px',
          padding: '0 2px'
        }}>
          {/* 左侧：交互按钮和数量 */}
          <Space size={12} direction="horizontal" style={{ lineHeight: 1.2 }}>
            {/* 点赞按钮和数量 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div 
                onClick={handleLike}
                style={{ 
                  position: 'relative',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: liked 
                    ? 'linear-gradient(135deg, #ff6b6b, #ff8e8e)'
                    : 'linear-gradient(135deg, #f5f5f5, #e0e0e0)',
                  boxShadow: liked 
                    ? '0 2px 8px rgba(255, 107, 107, 0.3)'
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  border: liked 
                    ? '2px solid transparent'
                    : '2px solid transparent',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'content-box, border-box'
                }}
              >
                {/* 彩色渐变边框 */}
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  right: '-2px',
                  bottom: '-2px',
                  borderRadius: '50%',
                  background: liked 
                    ? 'linear-gradient(135deg, #ff6b6b, #ff8e8e, #ffb300)'
                    : 'linear-gradient(135deg, #d32f2f, #ff9800, #ffb300)',
                  zIndex: -1
                }} />
                
                <HeartOutlined style={{ 
                  color: liked ? '#ffffff' : '#ff6b6b', 
                  fontSize: '16px',
                  transition: 'color 0.3s'
                }} />
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: liked ? '#ff6b6b' : '#666',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {likeCount}
              </span>
            </div>
            
            {/* 收藏按钮和数量 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div 
                onClick={handleFavorite}
                style={{ 
                  position: 'relative',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: favorited 
                    ? 'linear-gradient(135deg, #ffd700, #ffeb3b)'
                    : 'linear-gradient(135deg, #f5f5f5, #e0e0e0)',
                  boxShadow: favorited 
                    ? '0 2px 8px rgba(255, 215, 0, 0.3)'
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  border: favorited 
                    ? '2px solid transparent'
                    : '2px solid transparent',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'content-box, border-box'
                }}
              >
                {/* 彩色渐变边框 */}
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  right: '-2px',
                  bottom: '-2px',
                  borderRadius: '50%',
                  background: favorited 
                    ? 'linear-gradient(135deg, #ffd700, #ffeb3b, #ff9800)'
                    : 'linear-gradient(135deg, #ffb300, #ff9800, #d32f2f)',
                  zIndex: -1
                }} />
                
                <StarOutlined style={{ 
                  color: favorited ? '#ffffff' : '#ffb300', 
                  fontSize: '16px',
                  transition: 'color 0.3s'
                }} />
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: favorited ? '#ffb300' : '#666',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {favoriteCount}
              </span>
            </div>
            
            {/* 观看图标和数量 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <EyeOutlined style={{ 
                fontSize: '11px', // 点赞图标16px的2/3 ≈ 11px
                color: '#1890ff' 
              }} />
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: '#1890ff',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {product.view_count || 0}
              </span>
            </div>
          </Space>
          
          
          {/* 右侧：打印时间和耗材 */}
          <div style={{ textAlign: 'right', fontSize: '11px' }}>
            <div style={{ fontWeight: 'bold', color: '#d32f2f', display: 'flex', alignItems: 'center', gap: '1px' }}>
              {product.print_duration ? `${Math.floor(product.print_duration / 60)}h` : '未知'}
              {product.material_consumption && product.print_duration && <span style={{ color: '#d9d9d9' }}>|</span>}
              {product.material_consumption ? `${product.material_consumption}g` : '耗材'}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// 独立图片显示组件
const FullImageDisplay: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {!imageLoaded && !imageError && (
        <Spin size="large" style={{ position: 'absolute', zIndex: 1 }} />
      )}
      
      {imageError ? (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #d32f2f, #ffcdd2)',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2
        }}>
          <PrinterOutlined style={{ fontSize: '64px', color: 'rgba(255, 255, 255, 0.8)' }} />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover', // 改为cover确保图片填充但不溢出
            display: imageLoaded ? 'block' : 'none',
            position: 'relative',
            zIndex: 3
          }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};

// 主页组件
function HomePage() {
  const [printerStatus, setPrinterStatus] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const navigate = useNavigate();

  const handleProductClick = (product: any) => {
    navigate(`/products/${product.id}`);
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 获取产品数据
        const productsData = await fetchPrintedProducts(activeCategory, searchKeyword);
        setProducts(productsData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeCategory, searchKeyword]);

  return (
    <Layout className="layout" style={{ minHeight: '100vh' }}>
      {/* 顶部导航栏 */}
      <Header style={{ 
        background: `url('/background.png') no-repeat center center`,
        backgroundSize: 'cover',
        padding: '0 20px', 
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        height: '80px',
        lineHeight: '80px',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          height: '100%'
        }}>
          {/* Logo和标题 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              overflow: 'hidden'
            }}>
              <img 
                src="/logo.png" 
                alt="3D打印共享仓库" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
              />
            </div>
            
            {/* 艺术字：For you star. For your dream. */}
            <div style={{
              fontFamily: "'Arial Black', 'Arial', sans-serif",
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              letterSpacing: '0.5px',
              lineHeight: '1.2'
            }}>
              For you star. For your dream.
            </div>
          </div>

          {/* 搜索栏 */}
          <SearchBar onSearch={handleSearch} />

          {/* 右侧信息 - 打印机管理按钮 */}
          {/* <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => navigate('/printers')}
              style={{
                background: '#d32f2f',
                borderColor: '#d32f2f',
                borderRadius: '20px',
                padding: '6px 20px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '500',
                boxShadow: '0 2px 4px rgba(211, 47, 47, 0.2)'
              }}
            >
              打印机管理
            </Button>
          </div> */}
        </div>
      </Header>
      
      <Content style={{ padding: "relative", maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          {/* 👇 新增：小红书风格横向选项卡（放在产品展示区最上方） */}
          <div style={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              marginBottom: '16px',
              // 隐藏滚动条（兼容所有浏览器）
              // '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              // 让选项卡容器内部居中（关键）
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                display: 'flex',
                gap: '20px', // 选项卡之间的间距（可调整）
                whiteSpace: 'nowrap',
                alignItems: 'center',
                // 均匀分布核心：space-around 让选项卡左右间距相等
                justifyContent: 'space-around',
                // 给容器加最小宽度，保证小屏滚动、大屏均匀分布
                minWidth: '100%',
                // 左右加少量padding，避免选项卡贴边
                padding: '0 10px'
              }}>
                {categories.map((category) => {
                  const isActive = activeCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: isActive ? '#d32f2f' : '#666',
                        fontSize: '15px',
                        fontWeight: isActive ? 600 : 400,
                        padding: '8px 12px', // 增加左右padding，按钮更饱满
                        cursor: 'pointer',
                        position: 'relative',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {category.icon && category.icon}
                        {category.name}
                      </span>
                      {/* 选中态下划线 */}
                      {isActive && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 'calc(100% - 20px)', // 适配新增的padding
                          height: '2px',
                          background: '#d32f2f',
                          borderRadius: '1px'
                        }}></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

        {/* 推荐栏 */}
        <FeaturedCarousel />

        {/* 瀑布式产品展示 */}
        <div style={{ marginTop: '8px' }}>
                    
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>加载中...</div>
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: '#fafafa', borderRadius: '8px' }}>
              <PrinterOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={5} style={{ color: '#999' }}>暂无作品</Title>
              <Text type="secondary">当前分类下还没有3D打印成品，请尝试其他分类</Text>
            </div>
          ) : (
            // 替换原瀑布流渲染区域的代码
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(170px, 25vw, 280px), 1fr))',
  
            // 限制最大列数：PC端最多4列，避免卡片过宽
            // gridTemplateColumns: 'repeat(min(4, auto-fill), minmax(clamp(140px, 40vw, 220px), 1fr))',
            gap: '12px', 
            width: '100%',
            padding: '0 4px',
            // 可选：限制容器最大宽度，避免超大屏卡片太宽
            maxWidth: 'none',
            margin: '0 auto'
          }}>
            {products.map((product) => (
              <div key={product.id} style={{ display: 'flex',minHeight: '0'  }}>
                <ProductCard product={product} onCardClick={handleProductClick} />
              </div>
            ))}
          </div>
          )}
        </div>
      </Content>
      
      <Footer style={{ 
        background: '#1f1f1f',
        padding: '40px 0 20px 0',
        color: '#ffffff',
        borderTop: '1px solid #333'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '30px' }}>
            {/* 左侧：平台信息 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: '#d32f2f',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <PrinterOutlined style={{ fontSize: '20px', color: 'white' }} />
                </div>
                <Title level={4} style={{ color: '#ffffff', margin: 0, fontWeight: 'bold' }}>
                  3D打印共享仓库
                </Title>
              </div>
              <Text style={{ color: '#b0b0b0', fontSize: '14px', lineHeight: '1.6' }}>
                个人云展示墙，展示自己打印的成果，分享创意与快乐。
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Space size="middle">
                  <Button 
                    type="text" 
                    icon={<HeartOutlined style={{ color: '#d32f2f' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    关注我
                  </Button>
                  <Button 
                    type="text" 
                    icon={<StarOutlined style={{ color: '#ffb300' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    收藏网站
                  </Button>
                </Space>
              </div>
            </div>

            {/* 右侧：二维码图片区域 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <Title level={5} style={{ color: '#ffffff', marginBottom: '16px', fontWeight: 'bold' }}>
                关注我
              </Title>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                {/* 第一张二维码 */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    padding: '8px',
                    margin: '0 auto 8px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img 
                      src="/uploads/xy.jpg" 
                      alt="闲鱼小铺" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                  <Text style={{ color: '#b0b0b0', fontSize: '12px' }}>
                    我的闲鱼小铺
                  </Text>
                </div>
                
                {/* 第二张二维码 */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    padding: '8px',
                    margin: '0 auto 8px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img 
                      src="/uploads/xhs.png" 
                      alt="小红书" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                  <Text style={{ color: '#b0b0b0', fontSize: '12px' }}>
                    小红书
                  </Text>
                </div>
              </div>
            </div>

          </div>

          {/* 分隔线 */}
          <Divider style={{ borderColor: '#333', margin: '20px 0' }} />

          {/* 底部：版权和备案信息 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div>
              <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                © {new Date().getFullYear()} 我的3D打印仓库 版权所有
              </Text>
            </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  豫ICP备2026001797号-1
                </Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EyeOutlined style={{ color: '#1890ff' }} />
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  备案
                </Text>
              </div>
            </div>
            

          </div>
        </div>
      </Footer>
    </Layout>
  );
}

// 内部组件：在 Router 上下文中渲染，可以使用 useLocation
function AppContent({ isAuthenticated, setIsAuthenticated }: { isAuthenticated: boolean; setIsAuthenticated: (value: boolean) => void }) {
  const location = useLocation();
  // OpenClaw弹窗状态
  const [openclawModalVisible, setOpenclawModalVisible] = useState(false);
  const [openclawModalCountdown, setOpenclawModalCountdown] = useState(15);

  const handleLoginSuccess = (token: string) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  // 检查是否应该显示弹窗
  const shouldShowModal = () => {
    // 检查是否在 jiyu-mailbox 页面
    if (location.pathname === '/jiyu-mailbox') {
      return false;
    }

    // 检查今天是否已点击"不提醒"
    const today = new Date().toDateString();
    const lastDismissed = localStorage.getItem('openclawModalDismissed');
    return (lastDismissed !== today);
  };

  // 初始化弹窗显示状态
  useEffect(() => {
    setOpenclawModalVisible(shouldShowModal());
  }, [location.pathname]);

  // OpenClaw弹窗倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (openclawModalVisible && openclawModalCountdown > 0) {
      timer = setInterval(() => {
        setOpenclawModalCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setOpenclawModalVisible(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [openclawModalVisible, openclawModalCountdown]);

  // 手动关闭弹窗
  const handleCloseOpenclawModal = () => {
    setOpenclawModalVisible(false);
  };

  // 今天不提醒
  const handleSkipToday = () => {
    const today = new Date().toDateString();
    localStorage.setItem('openclawModalDismissed', today);
    setOpenclawModalVisible(false);
  };

  // 跳转到寄语信箱页面
  const handleGoToJiyuMailbox = () => {
    window.location.href = '/jiyu-mailbox';
    setOpenclawModalVisible(false);
  };

  return (
    <>
      {/* 寄语信箱 - 暖心的定时提醒机器人 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '24px' }}>💌</span>
            <span style={{ fontWeight: 'bold', fontSize: '18px' }}>寄语信箱 - 暖心的定时提醒机器人</span>
          </div>
        }
        open={openclawModalVisible}
        onCancel={handleCloseOpenclawModal}
        footer={[
          <Button key="skipToday" onClick={handleSkipToday}>
            今天不提醒
          </Button>,
          <Button key="skip" onClick={handleCloseOpenclawModal}>
            跳过 ({openclawModalCountdown}秒后自动关闭)
          </Button>,
          <Button
            key="go"
            type="primary"
            onClick={handleGoToJiyuMailbox}
            style={{
              backgroundColor: '#ff6b6b',
              borderColor: '#ff6b6b',
              fontWeight: 'bold'
            }}
          >
            💝 体验寄语信箱功能
          </Button>,
        ]}
        closable={true}
        maskClosable={false}
        width={400}
        style={{ top: '20%' }}
      >
        <div style={{ padding: '15px 0' }}>
          <Paragraph style={{ fontSize: '15px', marginBottom: '15px', lineHeight: 1.5, textAlign: 'center' }}>
            我正在测试一个<strong style={{ color: '#ff6b6b' }}>「寄语信箱」</strong>功能，通过小龙虾AI模型，为你的重要关系发送<strong>暖心的定时提醒</strong>。
          </Paragraph>

          <div style={{
            backgroundColor: '#fff9f9',
            border: '1px solid #ffcccc',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>🤖</div>
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>微信机器人配置</div>
              </div>

              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>💌</div>
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>提醒内容配置</div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f0f9ff',
              borderRadius: '6px',
              padding: '12px',
              border: '1px solid #91d5ff',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                🧠 AI智能分析，生成暖心提醒
              </div>
              <div style={{ color: '#666', fontSize: '12px' }}>
                配置对象信息、提醒时间，让小龙虾在重要时刻发送暖心消息
              </div>
            </div>
          </div>

          <Paragraph type="secondary" style={{
            fontSize: '13px',
            textAlign: 'center',
            padding: '8px',
            color: '#666'
          }}>
            点击<strong style={{ color: '#ff6b6b' }}>「体验寄语信箱功能」</strong>开始配置
          </Paragraph>
        </div>
      </Modal>

      <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/printers" element={<PrinterOverviewPage />} />
          <Route path="/printers/:id" element={<PrinterDetailPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/jiyu-mailbox" element={<JiyuMailboxPage />} />

          {/* 管理路由 */}
          <Route
            path="/admin/login"
            element={<AdminLogin onLoginSuccess={handleLoginSuccess} />}
          />
          <Route
            path="/admin/*"
            element={<AdminLayout onLogout={handleLogout} />}
          />
        </Routes>
    </>
  );
}


 function App() {                                                                    
    const [isAuthenticated, setIsAuthenticated] = useState(false);                                                            
    return (                                                                          
      <Router>                                                                        
        <AppContent isAuthenticated={isAuthenticated}                                 
  setIsAuthenticated={setIsAuthenticated} />                                          
      </Router>                                                                       
    );                                                                                
  } 
export default App;
